import React, { useState, useEffect } from 'react';
import { StageProps, MatchCandidate, SeriesParticipant } from '../types';
import { AIService } from '../services/aiService';

const ParticipantMatching: React.FC<StageProps> = ({ appData, setAppData, onNext, onPrev }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchCandidates, setMatchCandidates] = useState<MatchCandidate[] | null>(null);

  useEffect(() => {
    if (appData.matchedParticipants) {
      setMatchCandidates(appData.matchedParticipants);
    }
  }, [appData.matchedParticipants]);

  const performMatching = async () => {
    if (!appData.processedRaceData) {
      setError('No processed race data available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const existingParticipants = appData.existingSeriesData || [];
      const matches = await AIService.matchParticipants(
        appData.processedRaceData,
        existingParticipants
      );

      setMatchCandidates(matches);
      setAppData({
        ...appData,
        matchedParticipants: matches
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match participants');
    } finally {
      setLoading(false);
    }
  };

  const updateMatchStatus = (participantId: string, status: 'auto' | 'review' | 'manual' | 'new', selectedMatch?: SeriesParticipant) => {
    if (!matchCandidates) return;

    const updatedCandidates = matchCandidates.map(candidate => {
      if (candidate.newParticipant.id === participantId) {
        return {
          ...candidate,
          status,
          selectedMatch
        };
      }
      return candidate;
    });

    setMatchCandidates(updatedCandidates);
    setAppData({
      ...appData,
      matchedParticipants: updatedCandidates
    });
  };

  const handleNext = () => {
    if (matchCandidates) {
      setAppData({
        ...appData,
        matchedParticipants: matchCandidates
      });
      onNext?.();
    }
  };

  const canProceed = matchCandidates && matchCandidates.length > 0;
  const hasExistingData = appData.existingSeriesData && appData.existingSeriesData.length > 0;

  // If no existing data, skip matching
  if (!hasExistingData) {
    return (
      <div className="stage-container">
        <div className="stage-header">
          <h2>👥 Participant Matching</h2>
          <p>No existing series data found. All participants will be treated as new.</p>
        </div>

        <div className="success">
          <strong>✅ No Matching Required</strong><br />
          Since this appears to be the first race in the series, all participants will be added as new entries.
        </div>

        <div className="stage-actions">
          <button className="btn btn-secondary" onClick={onPrev}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={onNext}>
            Next: View Results →
          </button>
        </div>
      </div>
    );
  }

  // Group participants by match type
  const autoMatches = matchCandidates?.filter(m => m.status === 'auto' && m.selectedMatch) || [];
  const newParticipants = matchCandidates?.filter(m => m.status === 'new' || (m.existingMatches.length === 0)) || [];
  const uncertainMatches = matchCandidates?.filter(m => 
    (m.status === 'review' || m.status === 'manual') && 
    m.existingMatches.length > 0 && 
    !m.selectedMatch
  ) || [];

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>👥 Participant Matching</h2>
        <p>Participants are matched only within the same category. Cross-category warnings will be displayed if the same name appears in different categories.</p>
      </div>

      {error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="file-info">
        <h4>📊 Matching Overview</h4>
        <p>
          New participants: {appData.processedRaceData?.length || 0}<br />
          Existing participants: {appData.existingSeriesData?.length || 0}
        </p>
      </div>

      {!matchCandidates && !loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <button 
            className="btn btn-primary" 
            onClick={performMatching}
            disabled={!appData.processedRaceData}
          >
            🤖 Match Participants
          </button>
        </div>
      )}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <span>Matching participants...</span>
        </div>
      )}

      {matchCandidates && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>🔍 Match Results</h3>
            <button 
              className="btn btn-secondary" 
              onClick={performMatching}
              disabled={loading}
            >
              🔄 Re-match
            </button>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
              <span style={{ color: '#22543d' }}>✅ Auto Matched: {autoMatches.length}</span>
              <span style={{ color: '#2b6cb0' }}>🆕 New Participants: {newParticipants.length}</span>
              <span style={{ color: '#d69e2e' }}>⚠️ Need Review: {uncertainMatches.length}</span>
              {matchCandidates.filter(m => m.crossCategoryWarning).length > 0 && (
                <span style={{ color: '#9f2c3e' }}>
                  🔀 Cross-Category Warnings: {matchCandidates.filter(m => m.crossCategoryWarning).length}
                </span>
              )}
            </div>
          </div>

          {/* Section 1: Auto Matches */}
          {autoMatches.length > 0 && (
            <div className="matching-section" style={{ marginBottom: '2rem' }}>
              <div className="matching-header">
                <h4>✅ Automatic Matches ({autoMatches.length})</h4>
                <span className="matching-count">100% confidence - auto-selected</span>
              </div>
              
              <div className="matching-table">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>New Participant</th>
                      <th>Matched With</th>
                      <th>Reason</th>
                      <th>Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoMatches.map((candidate) => (
                      <tr key={candidate.newParticipant.id}>
                        <td>
                          <strong>{candidate.newParticipant.name}</strong><br />
                          <small style={{ color: '#718096' }}>
                            {candidate.newParticipant.category} • Position {candidate.newParticipant.position}
                          </small>
                        </td>
                        <td>
                          <strong>{candidate.selectedMatch?.name}</strong><br />
                          <small style={{ color: '#718096' }}>
                            {candidate.selectedMatch?.totalPoints} total points
                          </small>
                        </td>
                        <td>
                          <small>{candidate.existingMatches[0]?.reasons.join(', ')}</small>
                        </td>
                        <td>
                          <select 
                            className="override-dropdown"
                            value={candidate.selectedMatch?.id || 'auto'}
                            onChange={(e) => {
                              if (e.target.value === 'new') {
                                updateMatchStatus(candidate.newParticipant.id!, 'new');
                              } else if (e.target.value === 'auto') {
                                // Keep current auto match
                              } else {
                                const selectedParticipant = appData.existingSeriesData?.find(p => p.id === e.target.value);
                                if (selectedParticipant) {
                                  updateMatchStatus(candidate.newParticipant.id!, 'auto', selectedParticipant);
                                }
                              }
                            }}
                          >
                            <option value="auto">✅ Keep Match</option>
                            <option value="new">🆕 Create New</option>
                            {appData.existingSeriesData
                              ?.slice()
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .filter(existing => existing.category === candidate.newParticipant.category)
                              .map(existing => (
                              <option key={existing.id} value={existing.id}>
                                {existing.name} ({existing.category})
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Section 2: New Participants */}
          {newParticipants.length > 0 && (
            <div className="matching-section" style={{ marginBottom: '2rem' }}>
              <div className="matching-header">
                <h4>🆕 New Participants ({newParticipants.length})</h4>
                <span className="matching-count">Will be added as new entries</span>
              </div>
              
              <div className="matching-table">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Participant</th>
                      <th>Category</th>
                      <th>Position</th>
                      <th>Manual Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newParticipants.map((candidate) => (
                      <tr key={candidate.newParticipant.id}>
                        <td><strong>{candidate.newParticipant.name}</strong></td>
                        <td>{candidate.newParticipant.category}</td>
                        <td>{candidate.newParticipant.position}</td>
                        <td>
                          <select 
                            className="override-dropdown"
                            value="new"
                            onChange={(e) => {
                              if (e.target.value !== 'new') {
                                const selectedParticipant = appData.existingSeriesData?.find(p => p.id === e.target.value);
                                if (selectedParticipant) {
                                  updateMatchStatus(candidate.newParticipant.id!, 'auto', selectedParticipant);
                                }
                              }
                            }}
                          >
                            <option value="new">🆕 New Participant</option>
                            {appData.existingSeriesData
                              ?.slice()
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .filter(existing => existing.category === candidate.newParticipant.category)
                              .map(existing => (
                              <option key={existing.id} value={existing.id}>
                                {existing.name} ({existing.category})
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Section 3: Uncertain Matches */}
          {uncertainMatches.length > 0 && (
            <div className="matching-section" style={{ marginBottom: '2rem' }}>
              <div className="matching-header">
                <h4>⚠️ Uncertain Matches ({uncertainMatches.length})</h4>
                <span className="matching-count">Require manual review</span>
              </div>
              
              <div style={{ padding: '1rem' }}>
                {uncertainMatches.map((candidate) => (
                  <div key={candidate.newParticipant.id} style={{ 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem',
                    background: 'white'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#2d3748' }}>
                        {candidate.newParticipant.name}
                      </h4>
                      <p style={{ margin: 0, color: '#718096', fontSize: '0.9rem' }}>
                        {candidate.newParticipant.category} • Position {candidate.newParticipant.position} • {candidate.newParticipant.points} points
                      </p>
                    </div>

                    <div className="match-options">
                      <h5 style={{ margin: '0 0 0.5rem 0', color: '#4a5568' }}>Choose an option:</h5>
                      
                      <div className="match-option">
                        <input
                          type="radio"
                          name={`match_${candidate.newParticipant.id}`}
                          checked={candidate.status === 'new'}
                          onChange={() => updateMatchStatus(candidate.newParticipant.id!, 'new')}
                        />
                        <div className="match-option-content">
                          <div className="match-option-title">🆕 Create as new participant</div>
                          <div className="match-option-details">
                            This person will be added as a new entry to the series
                          </div>
                        </div>
                      </div>

                      {candidate.existingMatches.map((match, matchIndex) => {
                        const confidenceClass = match.confidence >= 80 ? 'confidence-high' : 
                                              match.confidence >= 60 ? 'confidence-medium' : 'confidence-low';
                        
                        return (
                          <div key={matchIndex} className="match-option">
                            <input
                              type="radio"
                              name={`match_${candidate.newParticipant.id}`}
                              checked={candidate.selectedMatch?.id === match.participant.id}
                              onChange={() => updateMatchStatus(candidate.newParticipant.id!, 'auto', match.participant)}
                            />
                            <div className="match-option-content">
                              <div className="match-option-title">
                                {match.participant.name}
                                <span className={`confidence-badge ${confidenceClass}`}>
                                  {match.confidence}% match
                                </span>
                              </div>
                              <div className="match-option-details">
                                {match.participant.category} • {match.participant.totalPoints} total points<br />
                                <strong>Why:</strong> {match.reasons.join(', ')}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cross-Category Warnings Section */}
          {matchCandidates.filter(m => m.crossCategoryWarning).length > 0 && (
            <div className="matching-section" style={{ marginBottom: '2rem', borderColor: '#9f2c3e' }}>
              <div className="matching-header" style={{ backgroundColor: '#fef5f5' }}>
                <h4 style={{ color: '#9f2c3e' }}>🔀 Cross-Category Warnings ({matchCandidates.filter(m => m.crossCategoryWarning).length})</h4>
                <span className="matching-count" style={{ color: '#9f2c3e' }}>Same names found in different categories</span>
              </div>
              
              <div style={{ padding: '1rem' }}>
                {matchCandidates
                  .filter(candidate => candidate.crossCategoryWarning)
                  .map((candidate) => (
                    <div key={candidate.newParticipant.id} style={{ 
                      border: '1px solid #fed7d7', 
                      borderRadius: '8px', 
                      padding: '1rem', 
                      marginBottom: '1rem',
                      background: '#fef5f5'
                    }}>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#9f2c3e' }}>
                          {candidate.newParticipant.name}
                        </h4>
                        <p style={{ margin: 0, color: '#718096', fontSize: '0.9rem' }}>
                          New participant in <strong>{candidate.newParticipant.category}</strong> category
                        </p>
                      </div>
                      
                      <div style={{ 
                        padding: '0.75rem', 
                        background: 'rgba(159, 44, 62, 0.1)', 
                        borderRadius: '6px',
                        marginBottom: '0.75rem'
                      }}>
                        <p style={{ margin: 0, color: '#9f2c3e', fontWeight: '500' }}>
                          {candidate.crossCategoryWarning?.message}
                        </p>
                      </div>
                      
                      <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                        <strong>Options:</strong>
                        <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                          <li>Create as new participant (recommended for different people with same name)</li>
                          <li>This person can compete in multiple categories with separate point tallies</li>
                          <li>Consider if this is the same person switching categories mid-series</li>
                        </ul>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="success" style={{ marginTop: '1rem' }}>
            <strong>📊 Summary</strong><br />
            {autoMatches.length} automatically matched • {newParticipants.length} new participants • {uncertainMatches.filter(m => m.selectedMatch).length}/{uncertainMatches.length} uncertain matches resolved
          </div>
        </div>
      )}

      <div className="stage-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          ← Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={!canProceed || loading || uncertainMatches.some(m => !m.selectedMatch && m.status !== 'new')}
        >
          Next: View Results →
        </button>
      </div>
    </div>
  );
};

export default ParticipantMatching; 